import React, { startTransition, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form } from 'reactstrap';
import { get, noop } from 'lodash-es';
import classNames from 'classnames';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Utils */
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { updateCreativeId } from 'site-modules/financing/utils/update-creative-id';
import { getVehicleType } from 'site-modules/financing/utils/financing-utils';
/* Components */
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { FormFieldErrorMessage } from 'site-modules/financing/components/shared/form-field-error-message/form-field-error-message';
import { NextButton } from 'site-modules/financing/components/shared/next-button/next-button';
import { BackButton } from 'site-modules/financing/components/shared/back-button/back-button';
import { FeedbackScreen } from 'site-modules/financing/components/feedback-screen/feedback-screen';
/* Constants */
import {
  PHONE_MASK,
  VALIDATORS,
  SSN_LAST_FOUR_FIELD_MASK,
  DIGIT_CHAR,
} from 'client/site-modules/financing/constants/validation';
import { LOOK_UP_STEP } from 'site-modules/shared/constants/financing/steps';
/* Tracking */
import { EventToolbox } from 'client/utils/event-toolbox';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import { random } from 'client/utils/seed-randomizers';
/* HOC */
import { connectedPreQualify } from 'site-modules/financing/components/pre-qualify/pre-qualify';

import './pre-qualification-found-step.scss';

export function PreQualificationFoundUI({
  validationErrors = {},
  enableNextBtn,
  fields = {},
  validate,
  fieldRef,
  isMatchingVIN,
  onSubmit = noop,
  goToPrevScreen = noop,
  isMobile = false,
  enabledNextBtn = false,
  vehicle = null,
  isLoadingFeedbackScreen = false,
  isLoading = false,
  setFormData = noop,
  isPreVinFlow = false,
  calculatorSelections = {},
}) {
  const iframeRef = useRef();
  const formRef = useRef();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const {
    firstName: firstNameError,
    lastName: lastNameError,
    phoneNumber: phoneNumberError,
    email: emailError,
    confirmEmail: confirmEmailError,
    zip: zipError,
  } = validationErrors;
  const {
    firstName: firstNameField,
    lastName: lastNameField,
    phoneNumber: phoneNumberField,
    email: emailField,
    confirmEmail: confirmEmailField,
    zip: zipField,
  } = fields;

  const [ssnMaskedValue, setSsnMaskedValue] = useState('');
  const [ssnMaskKey, setSsnMaskKey] = useState(random());
  const [ssnValue, setSsnValue] = useState('');
  const [isSsnTouched, setIsSsnTouched] = useState(false);
  const [areEmailsEqual, setAreEmailsEqual] = useState(true);

  const isConfirmEmailValid = !confirmEmailError && areEmailsEqual;
  const isSsnValid = () => validation.validateSSNLastFour(ssnValue);
  const isSsnTouchedAndInvalid = isSsnTouched && !isSsnValid();

  useEffect(
    () => {
      const isValid = isFormValid && isSsnValid && areEmailsEqual;

      if (isFormUpdated) {
        setFormData({
          [`firstName`]: firstNameField && firstNameField.value(),
          [`lastName`]: lastNameField && lastNameField.value(),
          [`phoneNumber`]: phoneNumberField && phoneNumberField.value(),
          [`email`]: emailField && emailField.value(),
          [`ssn`]: ssnValue,
          [`zip`]: zipField && zipField.value(),
        });

        enableNextBtn(isValid);
      }
      startTransition(() => setIsFormUpdated(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFormValid, isFormUpdated, areEmailsEqual]
  );

  const handleBlur = (e, fieldName) => {
    const isFieldNameSsn = fieldName === 'ssn';

    if (isFieldNameSsn) {
      setSsnMaskKey(random());
      setSsnMaskedValue(ssnValue.replace(/\d/g, 'X'));
      setIsSsnTouched(true);
    }

    if (fieldName === 'confirmEmail' || fieldName === 'email') {
      const confirmEmailExists = confirmEmailField && confirmEmailField.value();
      setAreEmailsEqual(confirmEmailExists ? emailField && emailField.value() === confirmEmailField.value() : true);
    }

    const isValidated = validate(e, fieldName);

    EventToolbox.fireCustomEvent(FINANCING_EVENTS.INPUT_VALIDATION, {
      target: e.target,
      data: {
        isValidated,
        isPrimary: false,
        name: fieldName,
      },
    });

    const isValid = isSsnValid() && validate();

    setIsFormValid(isValid);
    setIsFormUpdated(true);
  };

  const handleFocus = () => {
    setIsFormUpdated(false);
    enableNextBtn(false);
  };

  const inputRef = field => {
    if (!field) {
      return undefined;
    }

    return fieldRef(field.props.name, field);
  };

  const handleSsnFocus = () => {
    setIsFormUpdated(false);
    enableNextBtn(false);

    if (isSsnTouched) {
      setSsnMaskedValue(ssnValue);
    }
  };

  const handleSsnChange = value => {
    const isUnMaskedValue = DIGIT_CHAR.test(value);
    if (isUnMaskedValue) {
      setSsnValue(value);
    }
  };

  const handleSubmit = async () => {
    if (iframeRef.current && formRef.current) {
      await onSubmit();
      formRef.current.submit();
      setIsFormSubmitted(true);
    }
  };

  function renderMetaData() {
    const metaData = {
      firstName: firstNameField && firstNameField.value(),
      lastName: lastNameField && lastNameField.value(),
      phoneNumber: phoneNumberField && phoneNumberField.value(),
      email: emailField && emailField.value(),
      ssnHidden: ssnValue,
      zip: zipField && zipField.value(),
      vin: get(vehicle, 'vin'),
      make: get(vehicle, 'vehicleInfo.styleInfo.make'),
      model: get(vehicle, 'vehicleInfo.styleInfo.model'),
      year: get(vehicle, 'vehicleInfo.styleInfo.year'),
      condition: getVehicleType(vehicle),
      mileage: get(vehicle, 'vehicleInfo.mileage'),
      salesPrice: get(vehicle, 'prices.advertisedPrice'),
      isCapOne: isMatchingVIN,
      isPreVinFlow,
      calculatorSelections: JSON.stringify(calculatorSelections),
    };

    return Object.keys(metaData).map(key => <input key={key} type="hidden" name={key} value={metaData[key]} />);
  }

  return (
    <>
      <iframe
        className="border-0"
        height={0}
        name="pre-qualify-frame"
        ref={iframeRef}
        sandbox="allow-scripts allow-same-origin"
        title="Pre-Qualify Form"
      />
      <Form method="POST" id="prequalify-form" innerRef={formRef} target="pre-qualify-frame" noValidate>
        {isLoadingFeedbackScreen ? (
          <FeedbackScreen activeStep={LOOK_UP_STEP} />
        ) : (
          <>
            <h1 className="size-30 text-center mx-1 mx-md-0">Already Pre-Qualified with Capital One?</h1>
            <Row
              className="pre-qualified-form mx-auto my-0 mt-1"
              data-tracking-parent={updateCreativeId({
                isPreVinFlow,
                isMatchingVIN,
                trackingParent: 'personal_lookup',
              })}
            >
              <Row className="justify-content-center">
                <div className="mb-0_25 fw-bold text-gray-darker size-16 px-1">
                  Enter your information to look up your pre-qualified terms
                </div>
                <div className="size-12 mb-1_5 text-gray-darker px-1 w-100">All fields are required.</div>
              </Row>
              <Row className="px-0_5 w-100">
                <Col md={6}>
                  <FormField
                    id="first-name"
                    className={classNames({ 'mb-0': firstNameError })}
                    type="text"
                    maxLength={255}
                    inputClassName="input-field"
                    ref={inputRef}
                    isValid={!firstNameError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    name="firstName"
                    placeholder="First Name"
                    ariaInvalid={!!firstNameError}
                    ariaDescribedBy={`first-name-errorMessage`}
                    label="First Name"
                    useFloatingLabel
                    autoComplete="given-name"
                  />
                  <FormFieldErrorMessage
                    id={`first-name-errorMessage`}
                    message={firstNameError}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </Col>
                <Col md={6}>
                  <FormField
                    id="last-name"
                    className={classNames({ 'mb-0': lastNameError })}
                    type="text"
                    maxLength={255}
                    inputClassName="input-field"
                    ref={inputRef}
                    isValid={!lastNameError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    name="lastName"
                    placeholder="Last Name"
                    ariaInvalid={!!lastNameError}
                    ariaDescribedBy="last-name-errorMessage"
                    label="Last Name"
                    useFloatingLabel
                    autoComplete="family-name"
                  />
                  <FormFieldErrorMessage
                    id="last-name-errorMessage"
                    message={lastNameError}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col xs={12}>
                  <FormFieldMask
                    id="tel"
                    className={classNames({ 'mb-0': phoneNumberError })}
                    type="tel"
                    inputClassName="input-field"
                    ref={inputRef}
                    isValid={!phoneNumberError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    mask={PHONE_MASK}
                    name="phoneNumber"
                    defaultValue=""
                    ariaInvalid={!!phoneNumberError}
                    ariaDescribedBy="tel-errorMessage"
                    label="Phone Number"
                    useFloatingLabel
                    autoComplete="tel-national"
                    aria-required
                    inputMode="numeric"
                  />
                  <FormFieldErrorMessage
                    id="tel-errorMessage"
                    message={get(validationErrors, 'phoneNumber')}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col xs={12}>
                  <FormField
                    id="email"
                    className={classNames({ 'mb-0': emailError })}
                    type="email"
                    maxLength={255}
                    inputClassName="input-field"
                    ref={inputRef}
                    isValid={!emailError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    name="email"
                    placeholder="Email Address"
                    ariaInvalid={!!emailError}
                    ariaDescribedBy="email-errorMessage"
                    label="Email Address"
                    useFloatingLabel
                    autoComplete="email"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id="email-errorMessage"
                    message={get(validationErrors, 'email')}
                    className="mt-0_25 mb-1 ps-1"
                  />

                  <FormField
                    id="confirm-email"
                    className={classNames({ 'mb-0': !isConfirmEmailValid })}
                    type="email"
                    maxLength={255}
                    inputClassName="input-field"
                    ref={inputRef}
                    isValid={isConfirmEmailValid}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    name="confirmEmail"
                    placeholder="Confirm Email Address"
                    ariaInvalid={!isConfirmEmailValid}
                    ariaDescribedBy="confirm-email-errorMessage"
                    label="Confirm Email Address"
                    useFloatingLabel
                    autoComplete="email"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id={`confirm-email-errorMessage`}
                    message={
                      areEmailsEqual
                        ? get(validationErrors, 'confirmEmail')
                        : 'Please check that your email is entered correctly.'
                    }
                    className="mt-0_25 mb-1 ps-1"
                  />
                </Col>
              </Row>
              <Row className="px-0_5 w-100">
                <Col md={8} className="ssn-field pos-r">
                  <FormFieldMask
                    id="ssn"
                    type="text"
                    className={classNames({ 'mb-0': isSsnTouchedAndInvalid })}
                    inputClassName="input-field mb-0_5 ps-3"
                    ref={inputRef}
                    isValid={!isSsnTouchedAndInvalid}
                    onBlur={handleBlur}
                    onChange={handleSsnChange}
                    onFocus={handleSsnFocus}
                    mask={SSN_LAST_FOUR_FIELD_MASK}
                    defaultValue={ssnMaskedValue}
                    name="ssn"
                    key={ssnMaskKey}
                    isTouched={isSsnTouched}
                    label="Last 4 Digits of your SSN"
                    labelClassName="ms-1_75"
                    useFloatingLabel
                    ariaInvalid={isSsnTouchedAndInvalid}
                    ariaDescribedBy="ssn-errorMessage"
                    ariaDescribedByAdditional="ssn-helpText"
                    autoComplete="off"
                    aria-required
                    inputMode="numeric"
                  />
                  <i className="icon-lock2 ssn-lock-icon size-20" aria-hidden />
                  <FormFieldErrorMessage
                    id="ssn-errorMessage"
                    message={isSsnTouchedAndInvalid ? 'Please enter 4 digits.' : ''}
                    className="mt-0_25 mb-1 ps-1"
                  />
                  <FormField
                    id="ssnHidden"
                    type="hidden"
                    ref={inputRef}
                    name="ssnHidden"
                    defaultValue={ssnValue}
                    hidden
                  />
                </Col>
                <Col md={4}>
                  <FormField
                    id="zip"
                    className={classNames({ 'mb-0': zipError })}
                    name="zip"
                    placeholder="ZIP Code"
                    ref={inputRef}
                    isValid={!zipError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    inputClassName="input-field"
                    ariaInvalid={!!zipError}
                    ariaDescribedBy="zip-errorMessage"
                    label="ZIP Code"
                    useFloatingLabel
                    inputMode="numeric"
                    autoComplete="postal-code"
                  />
                  <FormFieldErrorMessage id="zip-errorMessage" message={zipError} className="mt-0_25 mb-1 ps-1" />
                </Col>
              </Row>
            </Row>
            <hr className="mt-1" />
            <div className="buttons-container d-flex mx-auto px-0_5 justify-content-between">
              <BackButton onClick={goToPrevScreen} />
              <NextButton
                onClick={handleSubmit}
                enabledNextBtn={enabledNextBtn}
                isFullWidth={isMobile}
                isLoading={isLoading}
                isSubmitBtn
              />
            </div>
          </>
        )}
        {!isFormSubmitted && renderMetaData()}
      </Form>
    </>
  );
}

PreQualificationFoundUI.propTypes = {
  vehicle: InventoryEntities.InventoryVin,
  fieldRef: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
  enableNextBtn: PropTypes.func,
  fields: PropTypes.shape({
    firstName: PropTypes.shape({
      value: PropTypes.func,
    }),
    lastName: PropTypes.shape({
      value: PropTypes.func,
    }),
    phoneNumber: PropTypes.shape({
      value: PropTypes.func,
    }),
    email: PropTypes.shape({
      value: PropTypes.func,
    }),
    confirmEmail: PropTypes.shape({
      value: PropTypes.func,
    }),
    ssn: PropTypes.shape({
      value: PropTypes.func,
    }),
    zip: PropTypes.shape({
      value: PropTypes.func,
    }),
  }),
  onSubmit: PropTypes.func,
  goToPrevScreen: PropTypes.func,
  enabledNextBtn: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLoadingFeedbackScreen: PropTypes.bool,
  isLoading: PropTypes.bool,
  setFormData: PropTypes.func,
  isPreVinFlow: PropTypes.bool,
  calculatorSelections: PropTypes.shape({}),
};

export const PreQualificationFound = connectedPreQualify(
  formValidation(insiderMethods(PreQualificationFoundUI), VALIDATORS)
);
